import { Box, Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { UseFieldArrayReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { SortableList } from '@/components/lists/DragAndDropList/SortableList';
import { useFormContext } from '@/components/react-hook-form';
import {
  calculateResiduaryRecipients,
  getAssetOrderingWarningIndices,
  getDeathSectionDistributionStatus,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';
import {
  DeathSectionDistributionStatus,
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { PathsOf } from '@/types/subform';

import { PrimaryClientDeathSectionRecipient } from './PrimaryClientDeathSectionRecipient';

interface PrimaryClientDeathSectionRecipientsProps {
  deathEvent: PrimaryClientDeathEvent;
  fieldArrayProps: Pick<
    UseFieldArrayReturn<
      DispositiveProvisionsFormV2Shape,
      | 'dispositiveProvisionsV2.firstDeath.recipients'
      | 'dispositiveProvisionsV2.secondDeath.recipients',
      '_id'
    >,
    'move' | 'fields'
  > & {
    remove: (index: number) => void;
  };
  /**
   * If provided, these IDs will be used to force expand recipient entries.
   * Note that these are useArrayField-assigned _ids, not recipientIds.
   */
  forceExpandIds: string[];
  /**
   * If true, always expand entries by default (eg: on load)
   */
  defaultExpandAll?: boolean;
}

export function PrimaryClientDeathSectionRecipients({
  deathEvent,
  fieldArrayProps: { remove, move, fields: recipients },
  forceExpandIds,
  defaultExpandAll = false,
}: PrimaryClientDeathSectionRecipientsProps) {
  const { control, setValue, getValues } =
    useFormContext<DispositiveProvisionsFormV2Shape>();

  const recipientsBasePath =
    `${getDeathEventBasePath(deathEvent)}.recipients` as const;

  const { disabled: disabledFromContext } = useFormFieldsDisabled();

  // Watch both the full recipients arrays to calculate warning logic once
  const [firstDeathRecipients, secondDeathRecipients] = useWatch({
    control,
    name: [
      `${getDeathEventBasePath(PrimaryClientDeathEvent.FIRST_DEATH)}.recipients`,
      `${getDeathEventBasePath(PrimaryClientDeathEvent.SECOND_DEATH)}.recipients`,
    ],
  });

  const {
    status,
    residuaryIndex,
    firstWarningIndex,
    assetOrderingWarningIndices,
    firstOrderingWarningIndex,
  } = useMemo(() => {
    const distributionStatus = getDeathSectionDistributionStatus(
      deathEvent,
      firstDeathRecipients,
      secondDeathRecipients
    );

    const currentRecipients =
      deathEvent === PrimaryClientDeathEvent.FIRST_DEATH
        ? firstDeathRecipients
        : secondDeathRecipients;

    const { residuaryIndex: calculatedResiduaryIndex } =
      calculateResiduaryRecipients(currentRecipients || []);

    const calculatedFirstWarningIndex =
      calculatedResiduaryIndex !== null ? calculatedResiduaryIndex + 1 : null;

    const orderingWarnings = getAssetOrderingWarningIndices(
      currentRecipients || []
    );

    // Find the first ordering warning index that won't be superseded by the
    // existing "fully distributes" warning (i.e., not after the residuary cutoff)
    let firstOrderingWarning: number | null = null;
    for (let i = 0; i < (currentRecipients?.length ?? 0); i++) {
      if (!orderingWarnings.has(i)) continue;
      const alsoHasResiduaryWarning =
        calculatedResiduaryIndex !== null && i > calculatedResiduaryIndex;
      if (!alsoHasResiduaryWarning) {
        firstOrderingWarning = i;
        break;
      }
    }

    return {
      status: distributionStatus,
      residuaryIndex: calculatedResiduaryIndex,
      firstWarningIndex: calculatedFirstWarningIndex,
      assetOrderingWarningIndices: orderingWarnings,
      firstOrderingWarningIndex: firstOrderingWarning,
    };
  }, [deathEvent, firstDeathRecipients, secondDeathRecipients]);

  const handleReorder = useCallback(
    (oldIndex: number, newIndex: number) => {
      move(oldIndex, newIndex);

      // move() shifts field values between indices but doesn't preserve
      // per-field dirty state. This causes useDefaultRecipientTransferTaxKind
      // to see isDirty=false and overwrite user-set transferTaxKind values.
      // Re-setting the values with shouldDirty marks them as user-modified.
      const minIdx = Math.min(oldIndex, newIndex);
      const maxIdx = Math.max(oldIndex, newIndex);
      for (let i = minIdx; i <= maxIdx; i++) {
        const fieldPath =
          `${recipientsBasePath}.${i}.transferTaxKind` as PathsOf<DispositiveProvisionsFormV2Shape>;
        const currentValue = getValues(fieldPath);
        if (currentValue) {
          setValue(fieldPath, currentValue as never, { shouldDirty: true });
        }
      }
    },
    [move, recipientsBasePath, getValues, setValue]
  );

  return (
    <Stack>
      <SortableList
        items={recipients}
        getId={(r) => r._id}
        onReorder={handleReorder}
        disabled={disabledFromContext}
      >
        <Box component="ul" sx={{ p: 0, m: 0, listStyleType: 'none' }}>
          {recipients.map((r, i) => {
            const isSecondDeathWithNoAssets =
              status === DeathSectionDistributionStatus.NO_ASSETS_REMAIN;

            // Flag recipients that come after the residuary distribution cut-off
            const needsWarning =
              isSecondDeathWithNoAssets ||
              (residuaryIndex !== null && i > residuaryIndex);

            // Only show the warning message for the first recipient that needs it
            const isFirstRecipientWithWarning =
              needsWarning &&
              (isSecondDeathWithNoAssets ? i === 0 : i === firstWarningIndex);

            // Asset ordering warning: asset DPs below entity-level DPs.
            // The existing "fully distributes" warning takes precedence.
            const needsOrderingWarning =
              !needsWarning && assetOrderingWarningIndices.has(i);

            return (
              <PrimaryClientDeathSectionRecipient
                key={r._id}
                recipientId={r._id}
                index={i}
                deathEvent={deathEvent}
                onRemove={() => remove(i)}
                forceExpandIds={forceExpandIds}
                defaultExpandAll={defaultExpandAll}
                totalRecipientsCount={recipients.length}
                needsWarning={needsWarning}
                isFirstRecipientWithWarning={isFirstRecipientWithWarning}
                needsOrderingWarning={needsOrderingWarning}
                isFirstRecipientWithOrderingWarning={
                  needsOrderingWarning && i === firstOrderingWarningIndex
                }
                disabled={disabledFromContext}
              />
            );
          })}
        </Box>
      </SortableList>
    </Stack>
  );
}
